import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Download, Eye, X, ChevronLeft, ChevronRight, Ticket } from "lucide-react";
import { SavedFiltersBar } from "@/components/SavedFiltersBar";

const STATUS_META = {
  borrador: { label: "Borrador", dot: "bg-slate-400", color: "bg-slate-100 text-slate-600 border-slate-200" },
  emitido: { label: "Emitido", dot: "bg-blue-500", color: "bg-blue-50 text-blue-700 border-blue-200" },
  enviado: { label: "Enviado", dot: "bg-violet-500", color: "bg-violet-50 text-violet-700 border-violet-200" },
  entregado: { label: "Entregado", dot: "bg-cyan-500", color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  canjeado: { label: "Canjeado", dot: "bg-emerald-500", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  caducado: { label: "Caducado", dot: "bg-amber-500", color: "bg-amber-50 text-amber-700 border-amber-200" },
  anulado: { label: "Anulado", dot: "bg-red-500", color: "bg-red-50 text-red-700 border-red-200" },
};

export default function VoucherList() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [center, setCenter] = useState("");
  const [centers, setCenters] = useState([]);
  const [page, setPage] = useState(0);
  const limit = 20;

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: limit.toString(), skip: (page * limit).toString() });
      if (search) params.append("search", search);
      if (status) params.append("status", status);
      if (type) params.append("type", type);
      if (center) params.append("center", center);
      const r = await axiosAuth().get(`/vouchers/?${params}`);
      setVouchers(r.data.vouchers || []);
      setTotal(r.data.total || 0);
    } catch { toast.error("Error al cargar bonos"); }
    finally { setLoading(false); }
  }, [axiosAuth, search, status, type, center, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axiosAuth().get("/vouchers/config").then(r => setCenters(r.data.centers || [])).catch(() => {});
  }, [axiosAuth]);

  const downloadPdf = async (id, code) => {
    try {
      const r = await axiosAuth().get(`/vouchers/pdf/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `bono_${code.replace(/-/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch { toast.error("Error al descargar PDF"); }
  };

  const clearFilters = () => { setSearch(""); setStatus(""); setType(""); setCenter(""); setPage(0); };
  const hasFilters = search || status || type || center;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-5" data-testid="voucher-list">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase tracking-tight">Bonos Emitidos</h1>
          <p className="text-sm text-slate-500 mt-1">{total} bono{total !== 1 ? "s" : ""} registrado{total !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={() => navigate("/admin/atenciones/emitir")} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-xs uppercase tracking-wider h-9 px-5 font-semibold" data-testid="new-voucher-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Emitir bono
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Buscar por codigo, cliente, matricula..."
            className="pl-9 rounded-sm h-9 text-xs border-slate-200" data-testid="voucher-search" />
        </div>
        <Select value={status || "all"} onValueChange={v => { setStatus(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-[120px] rounded-sm h-9 text-xs border-slate-200 font-semibold" data-testid="status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type || "all"} onValueChange={v => { setType(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-[140px] rounded-sm h-9 text-xs border-slate-200 font-semibold"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="lavado">Lavado</SelectItem>
            <SelectItem value="cortesia">Cortesia</SelectItem>
            <SelectItem value="atencion_comercial">Atencion comercial</SelectItem>
            <SelectItem value="cheque_regalo">Cheque regalo</SelectItem>
            <SelectItem value="personalizado">Personalizado</SelectItem>
          </SelectContent>
        </Select>
        {centers.length > 0 && (
          <Select value={center || "all"} onValueChange={v => { setCenter(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-[140px] rounded-sm h-9 text-xs border-slate-200 font-semibold"><SelectValue placeholder="Centro" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 px-3 text-xs text-slate-400 hover:text-slate-700">
            <X className="w-3 h-3 mr-1" /> Limpiar
          </Button>
        )}
      </div>
      <SavedFiltersBar module="vouchers"
        currentFilters={{ search, status, type, center }}
        onLoadFilter={(f) => { setSearch(f.search || ""); setStatus(f.status || ""); setType(f.type || ""); setCenter(f.center || ""); setPage(0); }}
      />

      {/* List */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>
          ) : vouchers.length === 0 ? (
            <div className="p-14 text-center">
              <Ticket className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No se encontraron bonos</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {vouchers.map(v => {
                const sm = STATUS_META[v.status] || STATUS_META.borrador;
                return (
                  <div key={v.id} className="p-4 hover:bg-slate-50/60 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/admin/atenciones/detalle/${v.id}`)} data-testid={`voucher-row-${v.code}`}>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap mb-1">
                          <span className="text-sm font-mono font-bold text-slate-900">{v.code}</span>
                          <Badge variant="outline" className={`h-5 text-[9px] rounded-sm font-semibold border ${sm.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sm.dot} mr-1`} />
                            {sm.label}
                          </Badge>
                          <span className="text-[10px] uppercase tracking-[0.1em] text-slate-400 font-semibold">
                            {v.template_snapshot?.name || v.type}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="font-medium text-slate-700">{v.client_name || "Sin nombre"}</span>
                          {v.plate && <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded-sm text-[10px]">{v.plate}</span>}
                          {v.center && <span>{v.center}</span>}
                          <span className="text-slate-400 font-mono text-[10px]">{v.issue_date || v.created_at?.slice(0, 10)}</span>
                          {v.expiry_date && <span className="text-slate-400 text-[10px]">Caduca: {v.expiry_date}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); downloadPdf(v.id, v.code); }}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700 rounded-sm" data-testid={`pdf-${v.code}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); navigate(`/admin/atenciones/detalle/${v.id}`); }}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700 rounded-sm">
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-slate-400 font-mono">{page * limit + 1}-{Math.min((page + 1) * limit, total)} de {total}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="h-8 w-8 p-0 rounded-sm">
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="h-8 w-8 p-0 rounded-sm">
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
